import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { acceptInvitation, rejectInvitation } from '../../services/api/team.service';
import { toast } from 'react-toastify';
import { CheckCircle, XCircle, Loader, AlertCircle, Shield, Users, Clock, X } from 'lucide-react';
import { useArtist } from '../../contexts/ArtistContext';
import { useUser } from '../../contexts/UserContext';
import { getFirstAccessibleRoute } from '../../utils/routePermissionUtils';

const AcceptInvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshTeams, refreshInvitations, setCurrentArtistId, myTeams, currentPermissions, pendingInvitations } = useArtist();
  const { userProfile } = useUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [showSwitchPrompt, setShowSwitchPrompt] = useState(false);
  const [acceptedInvitation, setAcceptedInvitation] = useState<{ artistId: number; artistName: string; teamId: number } | null>(null);
  const [pendingTeamId, setPendingTeamId] = useState<number | null>(null);
  
  // Get system admin status
  const getSystemRole = (): string | null => {
    try {
      return sessionStorage.getItem('systemRole') || userProfile?.systemRole || null;
    } catch {
      return null;
    }
  };
  const isSystemAdmin = getSystemRole() === 'system_admin';

  // Cleanup timeout ref for fallback navigation
  const fallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Watch for myTeams updates to show prompt after team is added
  useEffect(() => {
    if (pendingTeamId && myTeams.length > 0 && !showSwitchPrompt) {
      const team = myTeams.find(t => t.teamId === pendingTeamId);
      if (team && team.artistId) {
        // Clear fallback timeout if prompt is shown
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }
        setAcceptedInvitation({
          artistId: team.artistId,
          artistName: team.artistName || 'Unknown Artist',
          teamId: pendingTeamId
        });
        setShowSwitchPrompt(true);
        setPendingTeamId(null);
      }
    }
  }, [myTeams, pendingTeamId, showSwitchPrompt]);

  useEffect(() => {
    if (token) {
      // The token is in the URL, but we need to fetch invitation details
      // Since the API doesn't have a GET endpoint for invitation by token,
      // we'll show a generic accept/reject interface
      setLoading(false);
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token]);

  const handleAccept = async () => {
    if (!token) {
      toast.error('Invalid invitation token');
      return;
    }

    try {
      setProcessing(true);
      const result = await acceptInvitation(token, false);
      toast.success('Invitation accepted successfully!');
      
      // Refresh teams to update available artists in sidebar
      await refreshTeams();
      // Refresh invitations to update notification count immediately
      await refreshInvitations();
      
      const acceptedTeamId = result.teamId;
      
      // Check if there are remaining pending invitations
      // Wait a bit for refreshInvitations to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // If no pending invitations remain, navigate to first category (don't show switch prompt)
      if (!pendingInvitations || pendingInvitations.length === 0) {
        // No pending invitations left, navigate to first accessible category
        // Don't switch artist context - user will do it manually
        const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        navigate(firstRoute);
        setProcessing(false);
        return;
      }
      
      // Set pending teamId to trigger useEffect that watches myTeams
      setPendingTeamId(acceptedTeamId);
      setProcessing(false);
      
      // Clear any existing fallback timeout
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
      
      // Fallback: if team not found after 2 seconds, navigate to first accessible route
      fallbackTimeoutRef.current = setTimeout(() => {
        setPendingTeamId((currentPending) => {
          if (currentPending === acceptedTeamId) {
            const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
            navigate(firstRoute);
            return null;
          }
          return currentPending;
        });
        fallbackTimeoutRef.current = null;
      }, 2000);
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation');
      toast.error(err.message || 'Failed to accept invitation');
      setProcessing(false);
    }
  };

  const handleSwitchToArtist = () => {
    if (acceptedInvitation) {
      // User manually chooses to switch - allow it
      setCurrentArtistId(acceptedInvitation.artistId);
      setShowSwitchPrompt(false);
      setAcceptedInvitation(null);
      // Wait for permissions to refresh, then navigate to first accessible route
      setTimeout(() => {
        // Get updated permissions after artist switch
        const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        navigate(firstRoute);
      }, 1000);
    }
  };

  const handleDismissSwitchPrompt = () => {
    setShowSwitchPrompt(false);
    // Don't switch artist - navigate to first accessible route
    if (acceptedInvitation) {
      setAcceptedInvitation(null);
      setTimeout(() => {
        const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        navigate(firstRoute);
      }, 500);
    }
  };

  const handleReject = async () => {
    if (!token) {
      toast.error('Invalid invitation token');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this invitation?')) return;

    try {
      setProcessing(true);
      await rejectInvitation(token);
      toast.success('Invitation rejected');
      
      // Refresh invitations to update notification count immediately
      await refreshInvitations();
      
      // Wait a bit for refreshInvitations to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check if there are remaining pending invitations
      // If no pending invitations remain, navigate to first accessible category
      if (!pendingInvitations || pendingInvitations.length === 0) {
        const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        navigate(firstRoute);
      } else {
        // Still have pending invitations, navigate to invitations page
        navigate('/invitations');
      }
    } catch (err: any) {
      console.error('Error rejecting invitation:', err);
      setError(err.message || 'Failed to reject invitation');
      toast.error(err.message || 'Failed to reject invitation');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto h-12 w-12 text-gray-400 animate-spin mb-4" />
          <div className="text-gray-600 font-family-messina-sans">Loading invitation...</div>
        </div>
      </div>
    );
  }

  if (error && !token) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full mx-4 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2 font-family-messina-modern">
            Invalid Invitation
          </h2>
          <p className="text-gray-600 font-family-messina-sans mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-nova-dashboard-900 min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-2xl w-full">
        <div className="text-center mb-6">
          <Shield className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h1 className="text-2xl font-semibold text-gray-900 mb-2 font-family-messina-modern">
            Team Invitation
          </h1>
          <p className="text-gray-600 font-family-messina-sans">
            You've been invited to join a team
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle size={20} />
              <span className="font-family-messina-sans">{error}</span>
            </div>
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500 font-family-messina-sans">Team</p>
                <p className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                  {invitationData?.teamName || 'Loading team details...'}
                </p>
              </div>
            </div>
            {invitationData?.artistName && (
              <div className="flex items-center gap-3">
                <Shield size={20} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 font-family-messina-sans">Artist</p>
                  <p className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                    {invitationData.artistName}
                  </p>
                </div>
              </div>
            )}
            {invitationData?.roles && invitationData.roles.length > 0 && (
              <div>
                <p className="text-sm text-gray-500 font-family-messina-sans mb-2">Roles</p>
                <div className="flex flex-wrap gap-2">
                  {invitationData.roles.map((role: string) => (
                    <span
                      key={role}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm font-family-messina-sans"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {invitationData?.message && (
              <div>
                <p className="text-sm text-gray-500 font-family-messina-sans mb-2">Message</p>
                <p className="text-gray-700 font-family-messina-sans italic bg-white p-3 rounded">
                  "{invitationData.message}"
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAccept}
            disabled={processing}
            className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-family-messina-sans flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? (
              <>
                <Loader size={16} className="animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                <span>Accept Invitation</span>
              </>
            )}
          </button>
          <button
            onClick={handleReject}
            disabled={processing}
            className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-family-messina-sans flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle size={16} />
            <span>Reject</span>
          </button>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-600 hover:text-gray-900 font-family-messina-sans"
          >
            Go to Dashboard
          </button>
        </div>
      </div>

      {/* Prompt to switch to artist after accepting invitation */}
      {showSwitchPrompt && acceptedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                Switch to Artist?
              </h3>
              <button
                onClick={handleDismissSwitchPrompt}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-gray-600 mb-6 font-family-messina-sans">
              You've accepted an invitation for <span className="font-medium">{acceptedInvitation.artistName}</span>. 
              Would you like to switch to this artist context now?
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleSwitchToArtist}
                className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans"
              >
                Switch to Artist
              </button>
              <button
                onClick={handleDismissSwitchPrompt}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcceptInvitationPage;

